// Kota (kotaOrg) defect taxonomy — verbatim from /home/shubham/Parameter.docx.
//
// Parameter.docx defines THREE tabs (Roadway Surface, Asset Management, Road
// Safety) with their own sub-category groupings. The same leaf label can
// appear under different sub-categories with different severities depending
// on the tab — e.g. "Signage – Poor Visibility (Night)" is Medium under
// Asset Management → Signages, but High under Road Safety → Signages &
// Overhead Structures. Tracking only one (category, severity) per label
// would lose that detail.
//
// Primary structure: KOTA_TAB_LABELS[tab] = [{ category, label, severity }, …]
// Order within each tab matches the table row order in the docx.
//
// Severity colour: 🔴 Red → 'high', 🟠 Orange → 'medium', 🟡 Yellow → 'low'.

const HIGH = 'high', MED = 'medium', LOW = 'low';

export const KOTA_TAB_LABELS = {
  // ── Tab 1: Roadway Surface Condition ──
  // Label strings here MUST match the exact `inference_info[].label` values
  // written by populate_kota_inference_info.py — that's how the GIS filter
  // joins KOTA_TAB_LABELS to cbByLabel. Drift = silent drop from the dropdown.
  pavement: [
    { category: 'Pavement Condition', label: 'Pavement Damage (Severe)',  severity: HIGH },
    { category: 'Pavement Condition', label: 'Potholes',                  severity: HIGH },
    { category: 'Pavement Condition', label: 'Settlement',                severity: HIGH },
    { category: 'Pavement Condition', label: 'Rutting',                   severity: HIGH },
    { category: 'Pavement Condition', label: 'Stripping/Delamination',    severity: MED  },
    { category: 'Pavement Condition', label: 'Cracking',                  severity: MED  },
    { category: 'Pavement Condition', label: 'Water Stagnation',          severity: MED  },
    { category: 'Pavement Condition', label: 'Unsealed Roads',            severity: LOW  },
    { category: 'Pavement Condition', label: 'Pavement Joint',            severity: LOW  },
    { category: 'Pavement Condition', label: 'Patching',                  severity: LOW  },
    { category: 'Shoulder Condition', label: 'Shoulder – Edge Drop',      severity: HIGH },
    { category: 'Shoulder Condition', label: 'Shoulder – Unevenness',     severity: MED  },
    { category: 'Shoulder Condition', label: 'Shoulder – Rain Cuts',      severity: MED  },
  ],

  // ── Tab 2: Asset Management ──
  assets: [
    { category: 'Signages',                          label: 'Damaged Sign Boards / Sign Structures',  severity: HIGH },
    { category: 'Signages',                          label: 'Signage – Poor Visibility (Day)',        severity: MED  },
    { category: 'Signages',                          label: 'Signage – Poor Visibility (Night)',      severity: MED  },
    { category: 'Highway Lighting',                  label: 'Damaged Highway Lights',                 severity: HIGH },
    { category: 'Highway Lighting',                  label: 'Non-Functional Highway Lights',          severity: HIGH },
    { category: 'Comparative / Change Detection',    label: 'Missing Assets (Street Lights)',         severity: HIGH },
    { category: 'Comparative / Change Detection',    label: 'Missing Assets (Signages)',              severity: HIGH },
    { category: 'Crash Barriers & Guard Rails',      label: 'Damaged Crash Barriers',                 severity: HIGH },
    { category: 'Crash Barriers & Guard Rails',      label: 'Damaged (MBCB) Metal Beam Crash Barrier',severity: HIGH },
    { category: 'Crash Barriers & Guard Rails',      label: 'Damaged (PGR) Pedestrian Guard Rail',    severity: HIGH },
    { category: 'Kerb & Median Condition',           label: 'Damaged Kerb',                           severity: HIGH },
    { category: 'Drainage',                          label: 'Damaged Drain Cover Slabs',              severity: HIGH },
    { category: 'Drainage',                          label: 'Missing Drain Cover Slabs',              severity: HIGH },
    { category: 'Footpath',                          label: 'Damaged Footpath Tiles / Paver Blocks',  severity: MED  },
    { category: 'Safety Devices',                    label: 'Damaged Blinkers',                       severity: HIGH },
    { category: 'Safety Devices',                    label: 'Damaged Attenuators',                    severity: HIGH },
    { category: 'Safety Devices',                    label: 'Damaged Delineators',                    severity: HIGH },
    { category: 'Safety Devices',                    label: 'Damaged Anti-Glare',                     severity: HIGH },
    { category: 'Road Studs and Markers',            label: 'Damaged Hazard Markers',                 severity: HIGH },
    { category: 'Road Studs and Markers',            label: 'Damaged Road Studs',                     severity: MED  },
    { category: 'Road Studs and Markers',            label: 'Road Studs – Poor Visibility (Day)',     severity: LOW  },
    { category: 'Road Studs and Markers',            label: 'Road Studs – Poor Visibility (Night)',   severity: LOW  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Bus Bay – Damaged Shelters',             severity: MED  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Bus Bay – Faded Markings',               severity: LOW  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Bus Bay – Damaged Signages',             severity: LOW  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Truck Lay By – Damaged Shelters',        severity: MED  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Truck Lay By – Faded Markings',          severity: LOW  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Truck Lay By – Damaged Signages',        severity: LOW  },
    { category: 'Plantation / Landscaping',          label: 'Missing Plants / Irregular Gaps (Median)',severity: MED },
    { category: 'Plantation / Landscaping',          label: 'Deteriorated or Damaged Plants (Median)',severity: MED  },
    { category: 'Plantation / Landscaping',          label: 'Reduced Visibility Due to Plantation Growth', severity: HIGH },
    { category: 'Unauthorized Activity',             label: 'Unauthorized Hoardings',                 severity: MED  },
  ],

  // ── Tab 3: Road Safety ──
  roadSafety: [
    { category: 'Signages & Overhead Structures',    label: 'Damaged Sign Boards / Sign Structures',  severity: HIGH },
    { category: 'Signages & Overhead Structures',    label: 'Signage – Poor Visibility (Day)',        severity: MED  },
    { category: 'Signages & Overhead Structures',    label: 'Signage – Poor Visibility (Night)',      severity: HIGH },
    { category: 'Highway Lighting',                  label: 'Damaged Highway Lights',                 severity: HIGH },
    { category: 'Highway Lighting',                  label: 'Non-Functional Highway Lights',          severity: HIGH },
    { category: 'Comparative / Change Detection',    label: 'Missing Assets (Street Lights)',         severity: HIGH },
    { category: 'Comparative / Change Detection',    label: 'Missing Assets (Signages)',              severity: HIGH },
    { category: 'Comparative / Change Detection',    label: 'Missing Assets (Guard Rails)',           severity: HIGH },
    { category: 'Rumble Strips and Markers',         label: 'Damaged Rumble Strips',                  severity: HIGH },
    { category: 'Rumble Strips and Markers',         label: 'Damaged Hazard Markers',                 severity: MED  },
    { category: 'Safety Devices',                    label: 'Damaged Blinkers',                       severity: HIGH },
    { category: 'Safety Devices',                    label: 'Damaged Attenuators',                    severity: HIGH },
    { category: 'Safety Devices',                    label: 'Damaged Delineators',                    severity: HIGH },
    { category: 'Safety Devices',                    label: 'Damaged Anti-Glare',                     severity: HIGH },
    { category: 'Pavement Markings',                 label: 'Faded Pavement Marking',                 severity: HIGH },
    { category: 'Pavement Markings',                 label: 'Pavement Marking – Poor Visibility (Day)',severity: MED },
    { category: 'Pavement Markings',                 label: 'Pavement Marking – Poor Visibility (Night)',severity: HIGH },
    { category: 'Work Zone Safety',                  label: 'Work Zone – Inadequate Signboard Visibility', severity: MED },
    { category: 'Work Zone Safety',                  label: 'Work Zone – Inadequate Barricading',     severity: HIGH },
    { category: 'Work Zone Safety',                  label: 'Work Zone – Poor Diversion Arrangement / Condition', severity: HIGH },
    { category: 'Crash Barriers & Guard Rails',      label: 'Damaged Crash Barriers',                 severity: HIGH },
    { category: 'Crash Barriers & Guard Rails',      label: 'Faded Painting Concrete Crash (CC) Barrier', severity: MED },
    { category: 'Crash Barriers & Guard Rails',      label: 'Damaged (MBCB) Metal Beam Crash Barrier',severity: HIGH },
    { category: 'Crash Barriers & Guard Rails',      label: 'Missing Assets (Guard Rails)',           severity: HIGH },
    { category: 'Crash Barriers & Guard Rails',      label: 'Damaged (PGR) Pedestrian Guard Rail',    severity: HIGH },
    { category: 'Crash Barriers & Guard Rails',      label: 'Barriers – Faded Painting Guard Rails',  severity: MED  },
    { category: 'Road Studs',                        label: 'Damaged Road Studs',                     severity: HIGH },
    { category: 'Road Studs',                        label: 'Road Studs – Poor Visibility (Day)',     severity: MED  },
    { category: 'Road Studs',                        label: 'Road Studs – Poor Visibility (Night)',   severity: HIGH },
    { category: 'Kerb & Median Condition',           label: 'Damaged Kerb',                           severity: HIGH },
    { category: 'Kerb & Median Condition',           label: 'Faded Kerb Painting',                    severity: MED  },
    { category: 'Kerb & Median Condition',           label: 'Median Separator Damaged',               severity: HIGH },
    { category: 'Kerb & Median Condition',           label: 'Median Separator Paint Faded',           severity: MED  },
    { category: 'Drainage',                          label: 'Manhole',                                severity: LOW  },
    { category: 'Unauthorized Activity',             label: 'Unauthorized Median Openings',           severity: HIGH },
    { category: 'Unauthorized Activity',             label: 'Unauthorized Signboards',                severity: MED  },
    { category: 'Unauthorized Activity',             label: 'General Encroachments',                  severity: MED  },
    { category: 'Unauthorized Activity',             label: 'Illegal Parking',                        severity: LOW  },
    { category: 'Unauthorized Activity',             label: 'Unauthorized Advertisement Board',       severity: LOW  },
    { category: 'Plantation / Landscaping',          label: 'Shoulder – Vegetation Growth',           severity: LOW  },
    { category: 'Plantation / Landscaping',          label: 'Missing Plants / Irregular Gaps (Median)',severity: MED },
    { category: 'Plantation / Landscaping',          label: 'Deteriorated or Damaged Plants (Median)',severity: MED  },
    { category: 'Plantation / Landscaping',          label: 'Reduced Visibility Due to Plantation Growth', severity: HIGH },
    { category: 'Plantation / Landscaping',          label: 'Excessive Plantation Growth',            severity: LOW  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Bus Bay – Damaged Shelters',             severity: MED  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Bus Bay – Faded Markings',               severity: LOW  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Bus Bay – Damaged Signages',             severity: LOW  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Truck Lay By – Damaged Shelters',        severity: MED  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Truck Lay By – Faded Markings',          severity: LOW  },
    { category: 'Bus Bay & Truck Lay-By Facilities', label: 'Truck Lay By – Damaged Signages',        severity: LOW  },
    { category: 'Cleanliness',                       label: 'Cleanliness – Litter',                   severity: LOW  },
    { category: 'Cleanliness',                       label: 'Cleanliness – Debris',                   severity: LOW  },
  ],
};

// Canonical category names — used by the Defect Breakdown / Defect Category
// Distribution rollups (one category per label). Stable, hand-picked to match
// the categorisation users have been seeing on the dashboard. Distinct from
// the per-tab category names in KOTA_TAB_LABELS, which mirror Parameter.docx
// verbatim and only drive the GIS / Reports filter dropdown layout.
export const KOTA_CATEGORIES = {
  PAVEMENT_CONDITION: 'Pavement Condition',
  SHOULDER_CONDITION: 'Shoulder Condition',
  KERB_MEDIAN:        'Kerb & Median Condition',
  PLANTATION:         'Plantation / Landscaping',
  DRAINAGE:           'Drainage',
  FOOTPATH:           'Footpath',
  CRASH_BARRIERS:     'Crash Barriers & Guard Rails',
  SIGNAGES:           'Signages & Overhead Structures',
  SAFETY_DEVICES:     'Safety Devices',
  ROAD_STUDS:         'Road Studs & Rumble Strips',
  PAVEMENT_MARKINGS:  'Pavement Markings',
  BUS_TRUCK_LAY_BY:   'Bus Bay & Truck Lay-By',
  HIGHWAY_LIGHTING:   'Highway Lighting',
  WORK_ZONE_SAFETY:   'Work Zone Safety',
  ENCROACHMENT:       'Unauthorized Activity / Encroachment',
  CLEANLINESS:        'Cleanliness',
  COMPARATIVE:        'Comparative / Change Detection',
};
const C = KOTA_CATEGORIES;

// Severity → hex for GIS markers (matches the global palette used elsewhere).
const SEVERITY_HEX = { high: '#DC3C3C', medium: '#E87A00', low: '#C8C800' };
const SEV_RANK = { high: 3, medium: 2, low: 1 };

const _norm = (label) =>
  (label || '').toString().toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

// Flat lookup used by the breakdown rollups + GIS marker colour. Each leaf
// gets ONE canonical category here (hand-picked) so the Total Defects
// Breakdown shows a stable, expected category set — independent of the
// per-tab disambiguation that the GIS filter (KOTA_TAB_LABELS) uses.
//
// Severity is the WORST-OF across tabs (e.g. signage at-night is High in
// Road Safety but Medium in Asset Management → stored as High here).
// Tabs are the union of tabs the label appears under.
const _PV    = ['pavement'];
const _AM    = ['assets'];
const _RS    = ['roadSafety'];
const _AM_RS = ['assets', 'roadSafety'];

export const KOTA_LABELS = {
  // ── Pavement Condition ──
  'pavement damage (severe)':          { severity: 'high',   tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'potholes':                          { severity: 'high',   tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'pothole':                           { severity: 'high',   tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'settlement':                        { severity: 'high',   tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'rutting':                           { severity: 'high',   tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'stripping / delamination':          { severity: 'medium', tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'stripping/delamination':            { severity: 'medium', tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'cracks':                            { severity: 'medium', tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'cracking':                          { severity: 'medium', tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'water stagnation':                  { severity: 'medium', tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'unsealed roads':                    { severity: 'low',    tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'unsealed road':                     { severity: 'low',    tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'pavement joint':                    { severity: 'low',    tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'patching':                          { severity: 'low',    tabs: _PV,    category: C.PAVEMENT_CONDITION },
  'patch':                             { severity: 'low',    tabs: _PV,    category: C.PAVEMENT_CONDITION },

  // ── Shoulder Condition ──
  'shoulder edge drop':                { severity: 'high',   tabs: _PV,    category: C.SHOULDER_CONDITION },
  'shoulder – edge drop':              { severity: 'high',   tabs: _PV,    category: C.SHOULDER_CONDITION },
  'shoulder unevenness':               { severity: 'medium', tabs: _PV,    category: C.SHOULDER_CONDITION },
  'shoulder – unevenness':             { severity: 'medium', tabs: _PV,    category: C.SHOULDER_CONDITION },
  'shoulder rain cut':                 { severity: 'medium', tabs: _PV,    category: C.SHOULDER_CONDITION },
  'shoulder – rain cuts':              { severity: 'medium', tabs: _PV,    category: C.SHOULDER_CONDITION },
  'shoulder – vegetation growth':      { severity: 'low',    tabs: _RS,    category: C.SHOULDER_CONDITION },

  // ── Signages & Overhead ──
  'damaged sign boards / sign structures':   { severity: 'high',   tabs: _AM_RS, category: C.SIGNAGES },
  'signage – poor visibility (day)':         { severity: 'medium', tabs: _AM_RS, category: C.SIGNAGES },
  'signage – poor visibility (night)':       { severity: 'high',   tabs: _AM_RS, category: C.SIGNAGES },

  // ── Highway Lighting ──
  'damaged highway lights':            { severity: 'high', tabs: _AM_RS, category: C.HIGHWAY_LIGHTING },
  'non-functional highway lights':     { severity: 'high', tabs: _AM_RS, category: C.HIGHWAY_LIGHTING },
  'missing assets (street lights)':    { severity: 'high', tabs: _AM_RS, category: C.COMPARATIVE },

  // ── Crash Barriers & Guard Rails ──
  'damaged crash barriers':                         { severity: 'high',   tabs: _AM_RS, category: C.CRASH_BARRIERS },
  'damaged (mbcb) metal beam crash barrier':        { severity: 'high',   tabs: _AM_RS, category: C.CRASH_BARRIERS },
  'damaged (pgr) pedestrian guard rail':            { severity: 'high',   tabs: _AM_RS, category: C.CRASH_BARRIERS },
  'missing assets (guard rails)':                   { severity: 'high',   tabs: _AM_RS, category: C.COMPARATIVE },
  'faded painting concrete crash (cc) barrier':     { severity: 'medium', tabs: _RS,    category: C.CRASH_BARRIERS },
  'barriers – faded painting guard rails':          { severity: 'medium', tabs: _RS,    category: C.CRASH_BARRIERS },

  // ── Kerb & Median ──
  'damaged kerb':                      { severity: 'high',   tabs: _AM_RS, category: C.KERB_MEDIAN },
  'faded kerb painting':               { severity: 'medium', tabs: _RS,    category: C.KERB_MEDIAN },
  'median separator damaged':          { severity: 'high',   tabs: _RS,    category: C.KERB_MEDIAN },
  'median separator paint faded':      { severity: 'medium', tabs: _RS,    category: C.KERB_MEDIAN },

  // ── Drainage ──
  'damaged drain cover slabs':         { severity: 'high', tabs: _AM,    category: C.DRAINAGE },
  'missing drain cover slabs':         { severity: 'high', tabs: _AM,    category: C.DRAINAGE },
  'manhole':                           { severity: 'low',  tabs: _RS,    category: C.DRAINAGE },
  'manhole cover':                     { severity: 'low',  tabs: _RS,    category: C.DRAINAGE },

  // ── Footpath ──
  'damaged footpath tiles / paver blocks':  { severity: 'medium', tabs: _AM, category: C.FOOTPATH },

  // ── Safety Devices ──
  'damaged blinkers':                  { severity: 'high', tabs: _AM_RS, category: C.SAFETY_DEVICES },
  'damaged attenuators':               { severity: 'high', tabs: _AM_RS, category: C.SAFETY_DEVICES },
  'damaged delineators':               { severity: 'high', tabs: _AM_RS, category: C.SAFETY_DEVICES },
  'damaged anti-glare':                { severity: 'high', tabs: _AM_RS, category: C.SAFETY_DEVICES },

  // ── Road Studs & Rumble Strips ──
  'damaged road studs':                   { severity: 'high',   tabs: _AM_RS, category: C.ROAD_STUDS },
  'road studs – poor visibility (day)':   { severity: 'medium', tabs: _AM_RS, category: C.ROAD_STUDS },
  'road studs – poor visibility (night)': { severity: 'high',   tabs: _AM_RS, category: C.ROAD_STUDS },
  'damaged rumble strips':                { severity: 'high',   tabs: _RS,    category: C.ROAD_STUDS },
  'damaged hazard markers':               { severity: 'high',   tabs: _AM_RS, category: C.ROAD_STUDS },

  // ── Pavement Markings ──
  'faded pavement marking':                     { severity: 'high',   tabs: _RS, category: C.PAVEMENT_MARKINGS },
  'pavement marking – poor visibility (day)':   { severity: 'medium', tabs: _RS, category: C.PAVEMENT_MARKINGS },
  'pavement marking – poor visibility (night)': { severity: 'high',   tabs: _RS, category: C.PAVEMENT_MARKINGS },

  // ── Bus Bay & Truck Lay-By ──
  'bus bay – damaged shelters':        { severity: 'medium', tabs: _AM, category: C.BUS_TRUCK_LAY_BY },
  'bus bay – faded markings':          { severity: 'low',    tabs: _AM, category: C.BUS_TRUCK_LAY_BY },
  'bus bay – damaged signages':        { severity: 'low',    tabs: _AM, category: C.BUS_TRUCK_LAY_BY },
  'truck lay by – damaged shelters':   { severity: 'medium', tabs: _AM, category: C.BUS_TRUCK_LAY_BY },
  'truck lay by – faded markings':     { severity: 'low',    tabs: _AM, category: C.BUS_TRUCK_LAY_BY },
  'truck lay by – damaged signages':   { severity: 'low',    tabs: _AM, category: C.BUS_TRUCK_LAY_BY },

  // ── Plantation / Landscaping ──
  'missing plants / irregular gaps (median)':    { severity: 'medium', tabs: _AM_RS, category: C.PLANTATION },
  'deteriorated or damaged plants (median)':     { severity: 'medium', tabs: _AM_RS, category: C.PLANTATION },
  'reduced visibility due to overgrowth':        { severity: 'high',   tabs: _AM_RS, category: C.PLANTATION },
  'reduced visibility due to plantation growth': { severity: 'high',   tabs: _AM_RS, category: C.PLANTATION },
  'excessive plantation growth':                 { severity: 'low',    tabs: _RS,    category: C.PLANTATION },

  // ── Work Zone Safety ──
  'work zone – inadequate signboard visibility':        { severity: 'medium', tabs: _RS, category: C.WORK_ZONE_SAFETY },
  'work zone – inadequate barricading':                 { severity: 'high',   tabs: _RS, category: C.WORK_ZONE_SAFETY },
  'work zone – poor diversion arrangement / condition': { severity: 'high',   tabs: _RS, category: C.WORK_ZONE_SAFETY },

  // ── Unauthorized Activity / Encroachment ──
  'unauthorized median opening':       { severity: 'high',   tabs: _RS,    category: C.ENCROACHMENT },
  'unauthorized median openings':      { severity: 'high',   tabs: _RS,    category: C.ENCROACHMENT },
  'unauthorized signboards':           { severity: 'medium', tabs: _RS,    category: C.ENCROACHMENT },
  'unauthorized hoardings':            { severity: 'medium', tabs: _AM_RS, category: C.ENCROACHMENT },
  'unauthorized advertisement board':  { severity: 'low',    tabs: _RS,    category: C.ENCROACHMENT },
  'illegal parking':                   { severity: 'low',    tabs: _RS,    category: C.ENCROACHMENT },
  'general encroachment':              { severity: 'medium', tabs: _RS,    category: C.ENCROACHMENT },
  'general encroachments':             { severity: 'medium', tabs: _RS,    category: C.ENCROACHMENT },

  // ── Cleanliness ──
  'cleanliness – litter':              { severity: 'low', tabs: _RS, category: C.CLEANLINESS },
  'cleanliness – debris':              { severity: 'low', tabs: _RS, category: C.CLEANLINESS },

  // ── Comparative / Change Detection (Missing Assets — signages variant) ──
  'missing assets (signages)':         { severity: 'high', tabs: _AM_RS, category: C.COMPARATIVE },
};

// Look up an entry using exact match then forgiving partial match.
const _lookupEntry = (rawLabel) => {
  const n = _norm(rawLabel);
  if (KOTA_LABELS[n]) return KOTA_LABELS[n];
  for (const [key, entry] of Object.entries(KOTA_LABELS)) {
    if (n.includes(key) || key.includes(n)) return entry;
  }
  return null;
};

export const kotaColorForLabel = (rawLabel) => {
  const entry = _lookupEntry(rawLabel);
  return entry ? SEVERITY_HEX[entry.severity] : null;
};

// Return dashboard tabs this label should surface under (union across tabs).
export const kotaTabsForLabel = (rawLabel) => {
  const entry = _lookupEntry(rawLabel);
  return entry ? [...entry.tabs] : ['roadSafety'];
};

// Return the mid-level category this label rolls up to. For per-tab
// disambiguation, prefer iterating KOTA_TAB_LABELS directly — this returns
// only the first-encountered category.
export const kotaCategoryForLabel = (rawLabel) => {
  const entry = _lookupEntry(rawLabel);
  return entry ? entry.category : 'Other';
};

// Worst-of severity across tabs.
export const kotaSeverityForLabel = (rawLabel) => {
  const entry = _lookupEntry(rawLabel);
  return entry ? entry.severity : 'medium';
};

// Roll up an array of {name, value|count} leaf entries into per-category
// entries: [{name: categoryName, value, severity, leafLabels: []}, ...].
// Used by the Total Defects Breakdown so each tile is a category, not a label.
export const rollUpKotaByCategory = (items = []) => {
  const buckets = {};
  for (const it of items) {
    const label = it.name || it.label || '';
    const value = typeof it.value === 'number'
      ? it.value
      : (typeof it.count === 'number' ? it.count : 0);
    if (!value) continue;
    const entry = _lookupEntry(label);
    if (!entry) continue;
    const cat = entry.category;
    if (!buckets[cat]) {
      buckets[cat] = { name: cat, value: 0, severity: entry.severity, leafLabels: [] };
    }
    buckets[cat].value += value;
    buckets[cat].leafLabels.push(label);
    if ((SEV_RANK[entry.severity] || 0) > (SEV_RANK[buckets[cat].severity] || 0)) {
      buckets[cat].severity = entry.severity;
    }
  }
  return Object.values(buckets).sort((a, b) => b.value - a.value);
};

// Is the current user operating under the Kota organization?
export const isKotaOrg = () => {
  try {
    const org = (window.localStorage.getItem('organization') || '').trim().toLowerCase();
    if (org === 'kotaorg') return true;
    const user = JSON.parse(window.localStorage.getItem('user') || '{}');
    return (user?.organization || '').toString().trim().toLowerCase() === 'kotaorg';
  } catch { return false; }
};
